/**
 * Weekly login digest — batch report for unreported tool sessions.
 */

import React, { useMemo, useState } from 'react';
import { X, Sparkles } from 'lucide-react';
import garageAPI from '../../../services/garage/api';
import { ROI_TIME_BUCKETS } from './GarageRoiReportModal';
import './GarageRoiPromptModals.css';

const GarageRoiDigestModal = ({
  open,
  tools = [],
  userName = '',
  onClose,
  onSaved,
}) => {
  const [selections, setSelections] = useState({}); // toolId -> minutes | 'skip'
  const [bucketLabels, setBucketLabels] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showSnooze, setShowSnooze] = useState(false);

  const greeting = useMemo(() => {
    const first = (userName || '').trim().split(/\s+/)[0];
    return first ? `Welcome back, ${first}` : 'Welcome back';
  }, [userName]);

  if (!open) return null;

  const setBucket = (toolId, bucket) => {
    setSelections((prev) => ({ ...prev, [toolId]: bucket.value }));
    setBucketLabels((prev) => ({ ...prev, [toolId]: bucket.label }));
  };

  const setSkip = (toolId) => {
    setSelections((prev) => ({ ...prev, [toolId]: 'skip' }));
    setBucketLabels((prev) => ({ ...prev, [toolId]: null }));
  };

  const allAnswered = tools.every((t) => selections[t.toolId] != null);

  const handleSubmit = async () => {
    if (!allAnswered) {
      setError('Pick a time (or Skip) for every tool before saving');
      return;
    }
    const reports = tools
      .filter((t) => selections[t.toolId] != null && selections[t.toolId] !== 'skip')
      .map((t) => ({
        toolId: t.toolId,
        minutesSaved: selections[t.toolId],
        bucketLabel: bucketLabels[t.toolId],
        source: 'digest',
      }));

    setSaving(true);
    setError(null);
    try {
      if (reports.length) {
        await garageAPI.submitRoiReports(reports);
      } else {
        await garageAPI.markRoiDigestShown();
      }
      // Skipped tools remain unreported by design; snooze banner until tomorrow
      // so the user isn't stuck with the Review banner after a partial digest.
      const hadSkips = tools.some((t) => selections[t.toolId] === 'skip');
      if (hadSkips) {
        try {
          await garageAPI.snoozeRoiPrompts('tomorrow');
        } catch (_) {
          /* ignore */
        }
      }
      onSaved?.({ saved: reports.length, snoozed: hadSkips ? 'tomorrow' : undefined });
      onClose?.();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleSnooze = async (duration) => {
    setSaving(true);
    setError(null);
    try {
      await garageAPI.snoozeRoiPrompts(duration);
      onSaved?.({ snoozed: duration });
      onClose?.();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to snooze');
    } finally {
      setSaving(false);
      setShowSnooze(false);
    }
  };

  const handleClose = async () => {
    // X ≈ snooze until tomorrow
    try {
      await garageAPI.snoozeRoiPrompts('tomorrow');
    } catch (_) {
      /* ignore */
    }
    onClose?.();
  };

  return (
    <div className="roi-prompt-overlay" onClick={handleClose}>
      <div className="roi-prompt-modal roi-digest-modal" onClick={(e) => e.stopPropagation()}>
        <div className="roi-prompt-header">
          <div>
            <div className="roi-prompt-kicker">
              <Sparkles size={14} /> {greeting}
            </div>
            <h3>Quick review before you dive in</h3>
            <p>
              {tools.length} tool{tools.length === 1 ? '' : 's'} with unreported sessions in the last
              7 days. Your reports calibrate admin estimates — declared ROI stays sessions ×
              min/session.
            </p>
          </div>
          <button
            type="button"
            className="roi-prompt-icon-btn"
            onClick={handleClose}
            title="Close · we'll ask again tomorrow"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="roi-prompt-body roi-digest-body">
          {tools.map((t) => (
            <div key={t.toolId} className="roi-digest-card">
              <div className="roi-digest-card-top">
                <div>
                  <div className="name">{t.name}</div>
                  <div className="meta">
                    {t.sessions} session{t.sessions === 1 ? '' : 's'}
                    {t.adminEstimate != null ? ` · team est. ${t.adminEstimate}m` : ''}
                    {t.lastUsedAt ? ` · last ${String(t.lastUsedAt).slice(0, 10)}` : ''}
                  </div>
                </div>
              </div>
              <div className="roi-bucket-row compact">
                {ROI_TIME_BUCKETS.map((b) => (
                  <button
                    key={b.label}
                    type="button"
                    className={`roi-bucket-btn ${
                      selections[t.toolId] === b.value ? 'active' : ''
                    }`}
                    onClick={() => setBucket(t.toolId, b)}
                  >
                    {b.label}
                  </button>
                ))}
                <button
                  type="button"
                  className={`roi-bucket-btn skip ${
                    selections[t.toolId] === 'skip' ? 'active' : ''
                  }`}
                  onClick={() => setSkip(t.toolId)}
                >
                  Skip
                </button>
              </div>
            </div>
          ))}
          {error && <div className="roi-prompt-error">{error}</div>}
        </div>

        <div className="roi-prompt-footer digest">
          <div className="roi-snooze-wrap">
            <button
              type="button"
              className="roi-prompt-secondary"
              onClick={() => setShowSnooze((v) => !v)}
              disabled={saving}
            >
              Snooze…
            </button>
            {showSnooze && (
              <div className="roi-snooze-menu">
                <button type="button" onClick={() => handleSnooze('later_today')}>
                  Later today
                </button>
                <button type="button" onClick={() => handleSnooze('tomorrow')}>
                  Tomorrow
                </button>
                <button type="button" onClick={() => handleSnooze('next_week')}>
                  Next week
                </button>
                <button type="button" className="danger" onClick={() => handleSnooze('opt_out')}>
                  Don&apos;t ask again
                </button>
              </div>
            )}
          </div>
          <button
            type="button"
            className="roi-prompt-primary"
            onClick={handleSubmit}
            disabled={saving || !allAnswered}
          >
            {saving ? 'Saving…' : 'Save reports'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GarageRoiDigestModal;
